import { makeAutoObservable } from "mobx";
import type RootStore from "../RootStore";
import { NetworkStore } from "./NetworkStore";
import { StorageStore } from "./StorageStore";
import { GeolocationStore } from "./GeolocationStore";
import { MediaStore } from "./MediaStore";
import { FileSystemStore } from "./FileSystemStore";
import { NotificationAPIStore } from "./NotificationAPIStore";
import { ClipboardStore } from "./ClipboardStore";
import { ShareStore } from "./ShareStore";
import { WakeLockStore } from "./WakeLockStore";

/**
 * BrowserAPIStore - Central hub for all browser API integrations
 * Provides access to various browser capabilities in a unified interface
 */
export class BrowserAPIStore {
  rootStore: RootStore;
  network: NetworkStore;
  storage: StorageStore;
  geolocation: GeolocationStore;
  media: MediaStore;
  fileSystem: FileSystemStore;
  notifications: NotificationAPIStore;
  clipboard: ClipboardStore;
  share: ShareStore;
  wakeLock: WakeLockStore;

  constructor(rootStore: RootStore) {
    makeAutoObservable(this, {
      rootStore: false,
    });
    this.rootStore = rootStore;

    // Initialize sub-stores
    this.network = new NetworkStore(rootStore);
    this.storage = new StorageStore(rootStore);
    this.geolocation = new GeolocationStore(rootStore);
    this.media = new MediaStore(rootStore);
    this.fileSystem = new FileSystemStore(rootStore);
    this.notifications = new NotificationAPIStore(rootStore);
    this.clipboard = new ClipboardStore(rootStore);
    this.share = new ShareStore(rootStore);
    this.wakeLock = new WakeLockStore(rootStore);
  }

  /**
   * Check browser capabilities
   */
  get capabilities() {
    return {
      network: "connection" in navigator,
      storage: "storage" in navigator && "estimate" in navigator.storage,
      geolocation: "geolocation" in navigator,
      camera:
        "mediaDevices" in navigator && "getUserMedia" in navigator.mediaDevices,
      microphone: "mediaDevices" in navigator,
      notifications: "Notification" in window,
      clipboard: "clipboard" in navigator,
      share: "share" in navigator,
      wakeLock: "wakeLock" in navigator,
      fileSystem: "showOpenFilePicker" in window,
      bluetooth: "bluetooth" in navigator,
      usb: "usb" in navigator,
      nfc: "NDEFReader" in window,
      payment: "PaymentRequest" in window,
      credentials: "credentials" in navigator,
      battery: "getBattery" in navigator,
      vibration: "vibrate" in navigator,
      idle: "IdleDetector" in window,
      screenCapture:
        "mediaDevices" in navigator &&
        "getDisplayMedia" in navigator.mediaDevices,
    };
  }

  /**
   * Request all necessary permissions
   */
  async requestPermissions() {
    const permissions = [];

    // Request notification permission
    if (this.capabilities.notifications) {
      permissions.push(this.notifications.requestPermission());
    }

    // Request geolocation permission
    if (this.capabilities.geolocation) {
      permissions.push(this.geolocation.requestPermission());
    }

    // Request camera/microphone permissions
    if (this.capabilities.camera) {
      permissions.push(this.media.requestCameraPermission());
    }

    return Promise.allSettled(permissions);
  }

  /**
   * Get current permission states
   */
  async getPermissionStates() {
    const states: Record<string, PermissionState | string> = {};

    try {
      // Check standard permissions
      const permissionNames: PermissionName[] = [
        "geolocation",
        "notifications",
        "camera",
        "microphone",
      ];

      for (const name of permissionNames) {
        try {
          const result = await navigator.permissions.query({ name });
          states[name] = result.state;
        } catch {
          states[name] = "unsupported";
        }
      }

      // Check notification permission separately (has different API)
      if ("Notification" in window) {
        states.notifications = Notification.permission;
      }
    } catch (error) {
      console.error("Error checking permissions:", error);
    }

    return states;
  }

  /**
   * Clean up all sub-stores
   */
  dispose() {
    // Dispose sub-stores that need cleanup
    this.geolocation.dispose();
    this.media.dispose();
    this.wakeLock.dispose();
  }
}
